/**
 * `EntityTable`＋`EntityFilterBar`：**一份欄位描述，兩種寬度**。
 *
 * 為什麼要有這個檔（plan-console-4zones-split.md T11 #10／#16）：
 * 身分頁、房間頁各寫過一次「表頭 → 列 → 手機版卡片」與自己的篩選／空態／載入態。
 * 複製出去的結果是行為各自漂移（一頁會排序、一頁不會；一頁手機漏欄位）。
 *
 * 這裡只抽**呈現**，不碰資料：
 * - `columns` 是唯一一份欄位描述；桌機 `<table>` 與手機卡片都從它長出來，所以兩邊欄位集合必然相同
 *   （每格都帶 `data-testid="entity-col-<id>"`，可以直接數）。
 * - 篩選與搜尋的**規則**留在呼叫端（例如身分頁的 `filterIdentityRows`），這一層只出 UI＋計數，
 *   不自己決定「哪幾列符合」。`filterBar` 是一個 slot，要放幾顆篩選鈕由頁面決定。
 * - 排序只吃 `column.sortValue()`；沒給 `sortValue` 的欄位不可排序（也不冒充可排序）。
 *
 * 硬規則：
 * - 樣式只走 `cui-entity-*`（`styles.css`）。**不在這裡寫 Tailwind utility**：
 *   本套件在宿主是以 build 好的 `dist` 被 Tailwind 掃描（`apps/console/src/index.css` 的 `@source`），
 *   新增的 utility 在 dist 追上之前不會生成——症狀是「本地看起來對、build 出來塌掉」。
 * - 手機（<768px）卡片列與桌機表格吃同一份 `columns`，`hideOnCard` 只在「桌機才有意義」時用
 *   （目前沒有用到；保留是為了讓例外必須明講，而不是悄悄漏掉）。
 */
import { useMemo, useState, type ReactNode } from 'react';

export interface EntityColumn<T> {
  /** 穩定 id：排序狀態、`data-testid="entity-col-<id>"`、CSS 都靠它。 */
  id: string;
  label: string;
  /** 桌機欄寬（`table-fixed` 的百分比，例如 `'26%'`）；不給就依內容均分。 */
  width?: string;
  align?: 'start' | 'end';
  /** 套在桌機 `<td>`／手機卡片欄位外框上的額外 class（例如 `cui-entity-cell--grow`）。 */
  className?: string;
  /** 排序值；不給＝這欄不可排序。`null`＝沒有值（永遠排在最後，不當 0）。 */
  sortValue?: (row: T) => string | number | null;
  render: (row: T) => ReactNode;
  /** 手機卡片不顯示這一欄（例外要明講；預設兩邊一致）。 */
  hideOnCard?: boolean;
}

export interface EntitySort {
  columnId: string;
  dir: 'asc' | 'desc';
}

export interface EntityEmpty {
  title: string;
  hint?: string;
  action?: { label: string; onClick: () => void };
}

export interface EntitySelection<T> {
  isPicked: (row: T) => boolean;
  /** 不給＝每一列都可勾。 */
  canPick?: (row: T) => boolean;
  /** 勾不起來時的說法（title），不要讓 checkbox 靜靜失效。 */
  reason?: (row: T) => string;
  onToggle: (row: T) => void;
  /** 有動作在跑時鎖住。 */
  busy?: boolean;
  /** 每一列 checkbox 的 aria-label。 */
  label: (row: T) => string;
}

export interface EntityRowAction<T> {
  label: (row: T) => string;
  onClick: (row: T) => void;
  disabled?: boolean | ((row: T) => boolean);
  testId?: string;
}

export interface EntityTableProps<T> {
  rows: readonly T[];
  columns: readonly EntityColumn<T>[];
  rowKey: (row: T) => string;
  /** `table`＝桌機表格＋手機卡片；`list`＝窄欄位清單（側欄房間那種），兩個寬度同一種長相。 */
  variant?: 'table' | 'list';
  caption?: string;
  loading?: boolean;
  /** 載入態骨架列數（預設 5）。 */
  loadingRows?: number;
  /** 沒有任何列（不是「篩選後沒有」）時顯示；篩選後的空態由呼叫端用 `filterBar` 下方自理或傳 `empty`。 */
  empty?: EntityEmpty;
  /** 篩選／搜尋 UI 的 slot，永遠在表格上方。 */
  filterBar?: ReactNode;
  /** 一進來就套用的排序；不給＝照 `rows` 原順序（不偷偷排序）。 */
  defaultSort?: EntitySort;
  rowAction?: EntityRowAction<T>;
  selection?: EntitySelection<T>;
  onRowClick?: (row: T) => void;
  rowSelected?: (row: T) => boolean;
  rowClassName?: (row: T) => string;
  /** 列的額外 data-* （例如 `data-state`）。 */
  rowAttrs?: (row: T) => Record<string, string>;
  testId?: string;
  rowTestId?: string;
}

/** 排序：`null` 永遠墊底；數字比大小、其餘比字串（localeCompare，中文照筆畫／拼音環境預設）。 */
function compareValues(a: string | number | null, b: string | number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

function sortRows<T>(rows: readonly T[], columns: readonly EntityColumn<T>[], sort: EntitySort | null): T[] {
  if (sort === null) return rows.slice();
  const column = columns.find((item) => item.id === sort.columnId);
  if (!column?.sortValue) return rows.slice();
  const pick = column.sortValue;
  const sign = sort.dir === 'asc' ? 1 : -1;
  return rows.slice().sort((left, right) => {
    const a = column.sortValue!(left);
    const b = column.sortValue!(right);
    // `null` 不參與方向反轉：不管 asc／desc，「沒有值」都排在最後。
    if (a === null || b === null) return compareValues(a, b);
    return sign * compareValues(a, b);
  }) as T[];
}

export function EntityTable<T>({
  rows,
  columns,
  rowKey,
  variant = 'table',
  caption,
  loading = false,
  loadingRows = 5,
  empty,
  filterBar,
  defaultSort,
  rowAction,
  selection,
  onRowClick,
  rowSelected,
  rowClassName,
  rowAttrs,
  testId,
  rowTestId,
}: EntityTableProps<T>) {
  const [sort, setSort] = useState<EntitySort | null>(defaultSort ?? null);
  const sorted = useMemo(() => sortRows(rows, columns, sort), [rows, columns, sort]);
  const isEmpty = !loading && rows.length === 0;

  const toggleSort = (column: EntityColumn<T>) => {
    if (!column.sortValue) return;
    setSort((current) => {
      if (current?.columnId !== column.id) return { columnId: column.id, dir: 'desc' };
      return { columnId: column.id, dir: current.dir === 'desc' ? 'asc' : 'desc' };
    });
  };

  const selectable = (row: T) => (selection?.canPick ? selection.canPick(row) : true);
  const actionDisabled = (row: T) =>
    typeof rowAction?.disabled === 'function' ? rowAction.disabled(row) : rowAction?.disabled === true;

  const checkbox = (row: T) =>
    selection ? (
      <input
        type="checkbox"
        className="cui-checkbox"
        checked={selection.isPicked(row)}
        disabled={!selectable(row) || selection.busy === true}
        title={selectable(row) ? selection.label(row) : (selection.reason?.(row) ?? selection.label(row))}
        aria-label={selection.label(row)}
        onChange={() => {
          if (selectable(row)) selection.onToggle(row);
        }}
        data-testid="entity-row-checkbox"
      />
    ) : null;

  const actionButton = (row: T, className: string) =>
    rowAction ? (
      <button
        type="button"
        className={className}
        disabled={actionDisabled(row)}
        onClick={(event) => {
          event.stopPropagation();
          rowAction.onClick(row);
        }}
        data-testid={rowAction.testId}
      >
        {rowAction.label(row)}
      </button>
    ) : null;

  const rowProps = (row: T) => ({
    ...(rowAttrs ? rowAttrs(row) : {}),
    ...(rowTestId ? { 'data-testid': rowTestId } : {}),
    ...(onRowClick
      ? {
          role: 'button' as const,
          tabIndex: 0,
          'aria-pressed': rowSelected ? rowSelected(row) : undefined,
          onClick: () => onRowClick(row),
          onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            onRowClick(row);
          },
        }
      : {}),
  });

  const skeleton = (
    <div className="cui-entity-skeleton" data-testid="entity-loading" aria-busy="true">
      {Array.from({ length: loadingRows }, (_, index) => (
        <span key={index} className="cui-entity-skeleton-row" />
      ))}
    </div>
  );

  const emptyBlock =
    isEmpty && empty ? (
      <div className="cui-entity-empty" data-testid="entity-empty">
        <p className="cui-entity-empty-title">{empty.title}</p>
        {empty.hint ? <p className="cui-entity-empty-hint">{empty.hint}</p> : null}
        {empty.action ? (
          <button type="button" className="cui-btn cui-btn--sm cui-btn--ghost" onClick={empty.action.onClick}>
            {empty.action.label}
          </button>
        ) : null}
      </div>
    ) : null;

  const body = loading ? (
    skeleton
  ) : variant === 'list' ? (
    <ul className="cui-entity-list">
      {sorted.map((row) => (
        <li
          key={rowKey(row)}
          className={['cui-entity-row--list', rowClassName?.(row) ?? ''].filter(Boolean).join(' ')}
          {...rowProps(row)}
        >
          {columns.map((column) => (
            <span
              key={column.id}
              className={['cui-entity-cell', column.className ?? ''].filter(Boolean).join(' ')}
              data-testid={`entity-col-${column.id}`}
            >
              {column.render(row)}
            </span>
          ))}
          {actionButton(row, `cui-btn cui-btn--sm${rowSelected?.(row) ? ' cui-btn--ghost' : ''}`)}
        </li>
      ))}
    </ul>
  ) : (
    <>
      <table className="cui-entity-table">
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead>
          <tr>
            {selection ? (
              <th scope="col" className="cui-entity-th cui-entity-th--pick">
                <span className="cui-entity-sr">選取</span>
              </th>
            ) : null}
            {columns.map((column) => {
              const sortedBy = sort?.columnId === column.id ? sort.dir : null;
              return (
                <th
                  key={column.id}
                  scope="col"
                  className="cui-entity-th"
                  style={column.width ? { width: column.width } : undefined}
                  aria-sort={sortedBy ? (sortedBy === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {column.sortValue ? (
                    <button
                      type="button"
                      className={`cui-entity-sort${sortedBy ? ' is-active' : ''}`}
                      onClick={() => toggleSort(column)}
                      data-testid={`entity-sort-${column.id}`}
                      data-dir={sortedBy ?? 'none'}
                    >
                      {column.label}
                      <span aria-hidden="true">{sortedBy === 'asc' ? '↑' : sortedBy === 'desc' ? '↓' : '↕'}</span>
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              );
            })}
            {rowAction ? (
              <th scope="col" className="cui-entity-th cui-entity-th--action">
                操作
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr
              key={rowKey(row)}
              className={['cui-entity-tr', rowClassName?.(row) ?? ''].filter(Boolean).join(' ')}
              {...rowProps(row)}
            >
              {selection ? <td className="cui-entity-td cui-entity-td--pick">{checkbox(row)}</td> : null}
              {columns.map((column) => (
                <td
                  key={column.id}
                  className={['cui-entity-td', column.className ?? ''].filter(Boolean).join(' ')}
                  data-testid={`entity-col-${column.id}`}
                >
                  {column.render(row)}
                </td>
              ))}
              {rowAction ? (
                <td className="cui-entity-td cui-entity-td--action">{actionButton(row, 'cui-btn cui-btn--sm')}</td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>

      {/* 手機（<768px）：一卡一列，欄位集合與上面那張表相同（同一份 `columns`）。 */}
      <ul className="cui-entity-cards">
        {sorted.map((row) => {
          const head = columns[0];
          const rest = columns.slice(1);
          return (
            <li
              key={rowKey(row)}
              className={['cui-entity-card', rowClassName?.(row) ?? ''].filter(Boolean).join(' ')}
              {...rowProps(row)}
            >
              <div className="cui-entity-card-head">
                {checkbox(row)}
                {head ? (
                  <span className={`cui-entity-cell cui-entity-cell--grow ${head.className ?? ''}`} data-testid={`entity-col-${head.id}`}>
                    {head.render(row)}
                  </span>
                ) : null}
                {actionButton(row, 'cui-btn cui-btn--sm')}
              </div>
              <div className="cui-entity-card-fields">
                {rest
                  .filter((column) => !column.hideOnCard)
                  .map((column) => (
                    <span
                      key={column.id}
                      className={['cui-entity-field', column.className ?? ''].filter(Boolean).join(' ')}
                      data-testid={`entity-col-${column.id}`}
                    >
                      <span className="cui-entity-field-label">{column.label}</span>
                      <span className="cui-entity-field-value">{column.render(row)}</span>
                    </span>
                  ))}
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );

  return (
    <div className="cui-entity" data-testid={testId} data-rows={rows.length} data-visible-rows={sorted.length}>
      {filterBar}
      {emptyBlock}
      {!isEmpty ? body : null}
    </div>
  );
}

export interface EntityFilterOption {
  id: string;
  label: string;
  count?: number;
}

export interface EntityFilterBarProps {
  /** 篩選鈕（`null`＝這一頁只搜尋）。 */
  options?: readonly EntityFilterOption[];
  value?: string;
  onChange?: (id: string) => void;
  query?: string;
  onQueryChange?: (query: string) => void;
  queryPlaceholder?: string;
  /** 搜尋框的 aria-label（螢幕閱讀器讀的那句）。 */
  queryLabel?: string;
  /** 「顯示 n / m」；兩個都給才顯示。 */
  shown?: number;
  total?: number;
  /** 篩選鈕的 data-testid 前綴（`<prefix>-<id>`）；預設 `entity-filter`。 */
  testIdPrefix?: string;
  testId?: string;
  /** 額外 slot（例如「清除篩選」）。 */
  children?: ReactNode;
}

/** 篩選條：左邊看法、右邊搜尋、尾巴計數。數字由呼叫端算好（同一份過濾規則），這裡不重算。 */
export function EntityFilterBar({
  options,
  value,
  onChange,
  query,
  onQueryChange,
  queryPlaceholder = '搜尋',
  queryLabel = '搜尋',
  shown,
  total,
  testIdPrefix = 'entity-filter',
  testId,
  children,
}: EntityFilterBarProps) {
  return (
    <div className="cui-entity-filters" data-testid={testId}>
      {options && options.length > 0 ? (
        <div role="group" aria-label="篩選" className="cui-entity-filter-group">
          {options.map((item) => {
            const active = value === item.id;
            return (
              <button
                key={item.id}
                type="button"
                className={`cui-btn cui-btn--sm${active ? '' : ' cui-btn--ghost'}`}
                aria-pressed={active}
                onClick={() => onChange?.(item.id)}
                data-testid={`${testIdPrefix}-${item.id}`}
                data-count={item.count}
              >
                {item.label}
                {item.count === undefined ? null : <span className="cui-entity-filter-count">{item.count}</span>}
              </button>
            );
          })}
        </div>
      ) : null}
      {onQueryChange ? (
        <input
          type="search"
          value={query ?? ''}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder={queryPlaceholder}
          aria-label={queryLabel}
          className="cui-entity-search"
          data-testid="entity-search"
        />
      ) : null}
      {shown !== undefined && total !== undefined ? (
        <span className="cui-entity-shown" role="status">
          顯示 {shown} / {total}
        </span>
      ) : null}
      {children}
    </div>
  );
}
